import { PickedFile } from '@capawesome/capacitor-file-picker';
import { ConcourseDb } from '../ConcourseDb';

import { PersonDoc } from 'common';
import { PeopleService, TeamDoc } from '../service/PeopleService';
import { blobToBase64, resizeImage } from '../utils/data';

const projectUrl = '/fn/projects';

export interface PeopleUpdateCallback {
  onComplete: (people: Person[]) => void;
  onChange?: (people: Person[]) => void;
  onError?: (error: any) => void;
}

export class Person {
  static getPeopleDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-people`);
  }
  /*
  static async updatePeopleDb(project: Project): Promise<{success: boolean, message: string}> {
    const accessToken = getAccessToken();
    if (accessToken === undefined) return {success: false, message: 'No access token'};

    console.log('syncing people database')

    const requestBody = {
      projectId: project.id
    }
    console.log('requestBody', JSON.stringify(requestBody))

    const response = await authFetch(`${globalThis.ccLambdaBaseUrl}${projectUrl}/sync-people-database`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(requestBody)
    });
    if(response.status === 200) {
      return {success: true, message: 'People database synced'}
    }
    return {success: false, message: `People database not synced: ${response.body}`}
  }
  static searchPeople(people: Person[], search: string): Person[] {
    if (!search || search.length == 0) return people;
    return people.filter(person => Person.personMatches(person, search));
  }

  static personMatches(person: Person, search: string): boolean {
    return (
      person.email.toLowerCase().includes(search.toLowerCase()) ||
      (person.first_name && person.first_name.toLowerCase().includes(search.toLowerCase())) ||
      (person.last_name && person.last_name.toLowerCase().includes(search.toLowerCase())) ||
      (person.company && person.company.toLowerCase().includes(search.toLowerCase())) ||
      (person.company_role && person.company_role.toLowerCase().includes(search.toLowerCase()))
    );
    }
    */
  /**
   * Simply method for getting people with 3 callback options.
   * @param project
   * @param syncOptions
   * @returns
   
  static async getPeople(projectId: string, syncOptions: PeopleUpdateCallback = null): Promise<Person[]> {
    const db = Person.getPeopleDb(projectId);

    if (syncOptions && syncOptions.onComplete) {
      const onCompleteDocs = async (docs: any[]) => {
        syncOptions.onComplete(await Person.parseDocs(db, docs));
      };
      db.on('complete', onCompleteDocs);
    }

    if (syncOptions && syncOptions.onChange) {
      const onChangeDocs = async () => {
        const docs = await db.getDocs();
        syncOptions.onChange(await Person.parseDocs(db, docs));
      };
      db.on('change', onChangeDocs);
    }

    if (syncOptions && syncOptions.onError) {
      db.on('error', syncOptions.onError);
    }
    return await Person.parseDocs(db, await db.getDocs());
  }

  static async getAllPeople(projectId: string, syncOptions: PeopleUpdateCallback = null): Promise<{ people: Person[]; removeListeners: () => void }> {
    const db = Person.getPeopleDb(projectId);
    const changeHandler = async () => {
      const docs = await db.getDocs();
      syncOptions?.onChange(await Person.parseDocs(db, docs));
    };

    const completeHandler = async (change: any) => {
      syncOptions?.onComplete(await Person.parseDocs(db, change?.docs));
    };

    const { addListeners, removeListeners } = ContractService.createDbListeners(db, syncOptions?.onError, changeHandler, completeHandler);
    addListeners();
    const people = await Person.parseDocs(db, await db.getDocs());
    return { people, removeListeners };
  }*/
  /**
   * Because the people db gets loaded almost constantly, we almost always have the latest version in the cache.
   * That means that this method is fairly safe to use, since it won't await a sync to return data.
   * @param projectId
   * @returns
  
  static async getPeopleAwait(projectId: string): Promise<Person[]> {
    const db = Person.getPeopleDb(projectId);
    return await Person.parseDocs(db, await db.getDocsAwaitSync());
  }

  static async parseDocs(db: ConcourseDb, docs: any[]): Promise<Person[]> {
    console.log('Person.parseDocs', docs);
    const people = docs
      .filter(d => d._id != 'lastSeq' && d.type != 'team')
      .map(async doc => {
        const pictureUrl = await db.getAttachmentUrl(doc._id, 'profilePicture');
        return new Person(doc, pictureUrl);
      });
    return await Promise.all(people);
  }

  static async savePerson(projectId: string, person: Person): Promise<Person> {
    const db = Person.getPeopleDb(projectId);
    return new Person(await db.saveDoc(person.doc), person.icon);
  }

  static async savePersonDoc(projectId: string, doc: PersonDoc): Promise<PersonDoc> {
    const db = Person.getPeopleDb(projectId);
    return await db.saveDoc(doc);
  }

  static findPersonByEmail(people: Person[], email: string): Person | undefined {
    return people.find(person => person.email === email);
  }

  static findPerson(people: Person[], id: string): Person {
    if (id == 'system')
      return new Person(
        { _id: 'system', first_name: 'System', last_name: '', email: '', company: '', company_role: '', created_by: 'system', created_date: '2020-01-01 00:00' },
        Person.defaultIcon,
      );
    return people.find(p => p.id == id);
  }

  static findPeople(people: Person[], ids: string[]): Person[] {
    return people.filter(p => ids.includes(p.id));
  }

  static getIcon(people: Person[]): string {
    return people.length == 1 ? people[0].icon : Team.groupIcon;
  }

    static getIconDocs(people: PersonDoc[]): string {
    return people.length == 1 ? people[0].image?.data : Team.groupIcon;
  }

  static getName(people: Person[]): string {
    return people.length == 1 ? people[0].fullName : `${people.length} people`;
  }
 */
  static defaultIcon: string = 'https://upload.wikimedia.org/wikipedia/commons/a/ac/Default_pfp.jpg';

  public doc: PersonDoc;
  public _pictureUrl: string;

  constructor(doc: PersonDoc, pictureUrl: string) {
    this.doc = doc;
    this._pictureUrl = pictureUrl;
  }

  get id(): string {
    return this.doc._id;
  }

  get first_name(): string {
    return this.doc.first_name;
  }

  get last_name(): string {
    return this.doc.last_name;
  }

  get email(): string {
    return this.doc.email;
  }

  get fullName(): string {
    return `${this.first_name} ${this.last_name}`;
  }

  get company(): string {
    return this.doc.company;
  }

  get company_role(): string {
    return this.doc.company_role;
  }

  /*
  get isUser(): boolean {
    return this.doc.type == "user"
  }
    */

  set iconBlob(value: Blob) {
    this._pictureUrl = URL.createObjectURL(value);
  }

  get icon(): string {
    return this.doc.image ? `data:${this.doc.image.mimeType};base64,${this.doc.image.data}` : '/assets/img/default-user.jpg';
  }

  contains(text: string) {
    return (
      this.id == text ||
      this.fullName.toLocaleLowerCase().includes(text.toLocaleLowerCase()) ||
      (this.email && this.email.toLocaleLowerCase().includes(text.toLocaleLowerCase())) ||
      (this.company && this.company.toLocaleLowerCase().includes(text.toLocaleLowerCase())) ||
      (this.company_role && this.company_role.toLocaleLowerCase().includes(text.toLocaleLowerCase()))
    );
  }
}


export class Team {
  static defaultIcon: string = 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b9/Group_font_awesome.svg/240px-Group_font_awesome.svg.png';
  static newTeam() {
    return new Team({ type: 'team', name: '', members: [] }, null);
  }
  static async getTeams(projectId: string, onComplete: Function = null, onChange = null, onError = null): Promise<Team[]> {
    const db = Person.getPeopleDb(projectId);

    if (onComplete) {
      db.on('complete', async (docs: any[]) => {
        onComplete(await Team.parseDocs(db, docs));
      });
    }

    if (onChange) {
      db.on('change', async () => {
        const docs = await db.getDocs();
        onChange(await Team.parseDocs(db, docs));
      });
    }

    if (onError) {
      db.on('error', onError);
    }
    return await Team.parseDocs(db, await db.getDocs());
  }

  static async parseDocs(db: ConcourseDb, docs: any[]): Promise<Team[]> {
    const teams = docs
      .filter(doc => doc.type === 'team')
      .map(async doc => {
        const pictureUrl = await db.getAttachmentUrl(doc._id, 'icon');
        return new Team(doc, pictureUrl);
      });
    return await Promise.all(teams);
  }

  static searchTeams(teams: Team[], search: string): Team[] {
    if (!search || search.length == 0) return teams;
    return teams.filter(team => Team.teamMatches(team, search));
  }
  static teamMatches(team: Team, search: string): boolean {
    return team.doc.name.toLowerCase().includes(search.toLowerCase());
  }

  static async saveTeam(projectId: string, team: Team): Promise<Team> {
    return new Team(await Person.getPeopleDb(projectId).saveDoc(team.doc), team.iconUrl);
  }

  static groupIcon: string = 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b9/Group_font_awesome.svg/240px-Group_font_awesome.svg.png';

  public doc: TeamDoc;
  private _iconUrl: string;

  constructor(doc: TeamDoc, iconUrl: string) {
    this.doc = doc;
    this._iconUrl = iconUrl;
  }

  get members(): string[] {
    return this.doc.members;
  }

  get iconUrl(): string {
    return this._iconUrl || Team.groupIcon;
  }
/*
  async setIcon(value: PickedFile) {
    const blob = await resizeImage(value.blob, 200, 200, 'image/webp');
    const base64 = await blobToBase64(blob);
    this.doc._attachments = this.doc._attachments || {};
    this.doc._attachments['icon'] = { content_type: blob.type, data: base64 };

    // clear the cached thumbnail url.
    this._iconUrl = URL.createObjectURL(blob);
  }
  */
 }
export class PersonOrTeam {
  static async getAwait(projectId: string): Promise<PersonOrTeam[]> {
    const db = Person.getPeopleDb(projectId);
    return await PersonOrTeam.parseDocs(db, await db.getDocsAwaitSync());
  }

  static async parseDocs(db: ConcourseDb, docs: any[]): Promise<PersonOrTeam[]> {
    const list = docs
      .filter(d => d._id != 'lastSeq')
      .map(async doc => {
        const pictureUrl = await db.getAttachmentUrl(doc._id, 'profilePicture');
        return new PersonOrTeam(doc, pictureUrl);
      });
    return await Promise.all(list);
  }

  static searchList(peopleOrTeams: PersonOrTeam[], search: string): PersonOrTeam[] {
    if (!search || search.length == 0) return peopleOrTeams;
    return peopleOrTeams.filter(pot => {
      return (pot.isPerson ? PeopleService.personMatches(pot.person.doc, search) : Team.teamMatches(pot.team, search)) ? pot : null;
    });
  }

  private doc: any;
  private _iconUrl: string;
  constructor(doc: any, iconUrl: string) {
    this.doc = doc;
    this._iconUrl = iconUrl;
  }

  get id(): string {
    return this.doc._id;
  }

  get isPerson() {
    return !!this.doc.email;
  }

  get person(): Person {
    return new Person(this.doc, this._iconUrl);
  }

  get team(): Team {
    return new Team(this.doc, this._iconUrl);
  }

  get name() {
    return this.isPerson ? this.person.fullName : this.team.doc.name;
  }

  get iconUrl() {
    return this.isPerson ? this.person.icon : this.team.iconUrl;
  }
}
